/**
 * SegmentationEngine — Uses MediaPipe Image Segmenter to separate
 * person from background for blur and custom background effects.
 *
 * Loads the selfie segmentation model lazily on first use.
 * Processes at ~15fps to balance quality vs performance on mobile.
 */

let _segmenterInstance = null;
let _loadingPromise = null;

async function getSegmenter() {
  if (_segmenterInstance) return _segmenterInstance;
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = (async () => {
    try {
      const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision');

      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/wasm'
      );

      _segmenterInstance = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
          delegate: 'GPU',
        },
        outputCategoryMask: true,
        runningMode: 'VIDEO',
      });

      console.log('[Segmentation] Model loaded successfully');
      return _segmenterInstance;
    } catch (err) {
      console.error('[Segmentation] Failed to load model:', err);
      _loadingPromise = null;
      return null;
    }
  })();

  return _loadingPromise;
}

export class SegmentationEngine {
  constructor() {
    this.segmenter = null;
    this.loading = false;
    this.ready = false;
    this.lastMask = null;
  }

  async init() {
    if (this.ready) return true;
    this.loading = true;
    console.log('[Segmentation] Initializing...');

    this.segmenter = await getSegmenter();
    this.loading = false;
    this.ready = !!this.segmenter;
    return this.ready;
  }

  /**
   * Segment a video frame. Returns the category mask where:
   * - 0 = background
   * - 1 = person
   */
  segment(videoElement, timestamp) {
    if (!this.segmenter || !this.ready) return null;

    try {
      const result = this.segmenter.segmentForVideo(videoElement, timestamp);
      if (result && result.categoryMask) {
        this.lastMask = result.categoryMask;
        return result.categoryMask;
      }
    } catch (err) {
      // Silently fail — will use last mask or no segmentation
    }
    return this.lastMask;
  }

  destroy() {
    this.segmenter = null;
    this.ready = false;
    this.lastMask = null;
    console.log('[Segmentation] Engine destroyed');
  }
}

export const PRESET_BACKGROUNDS = [
  { id: 'blur', label: 'Blur', type: 'blur', preview: 'linear-gradient(135deg, #667eea22, #764ba222)' },
  { id: 'heavy-blur', label: 'Heavy Blur', type: 'blur-heavy', preview: 'linear-gradient(135deg, #667eea44, #764ba244)' },
  { id: 'gradient-purple', label: 'Purple', type: 'gradient', value: 'linear-gradient(135deg, #1a0533, #4a1a7a, #2d1b69)', preview: 'linear-gradient(135deg, #1a0533, #4a1a7a)' },
  { id: 'gradient-sunset', label: 'Sunset', type: 'gradient', value: 'linear-gradient(135deg, #ff6b35, #d4145a, #1a1a2e)', preview: 'linear-gradient(135deg, #ff6b35, #d4145a)' },
  { id: 'gradient-ocean', label: 'Ocean', type: 'gradient', value: 'linear-gradient(135deg, #0c2340, #1565c0, #0d47a1)', preview: 'linear-gradient(135deg, #0c2340, #1565c0)' },
  { id: 'gradient-forest', label: 'Forest', type: 'gradient', value: 'linear-gradient(135deg, #0a1f0a, #1b5e20, #2e7d32)', preview: 'linear-gradient(135deg, #0a1f0a, #1b5e20)' },
  { id: 'solid-black', label: 'Black', type: 'solid', value: '#000000', preview: '#000000' },
  { id: 'solid-white', label: 'White', type: 'solid', value: '#ffffff', preview: '#ffffff' },
];
